import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageMeta from "../components/PageMeta";
import CTABand from "../components/CTABand";
import { PROJECTS } from "../data/content";

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);

  if (!project) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="project-not-found">
        <div className="text-center">
          <p className="font-heading text-2xl font-semibold text-ink">Project not found.</p>
          <Link to="/projects" className="btn-outline mt-6">Back to projects</Link>
        </div>
      </main>
    );
  }

  const facts = [
    ["Client / Facility", project.client],
    ["Location", project.location],
    ["Facility Type", project.facility_type],
    ["Project Scope", project.scope],
    ["Capacity", project.capacity],
    ["Current Status", project.status],
  ];

  return (
    <main className="pt-[76px]" data-testid="project-detail-page">
      <PageMeta title={`${project.name} | AccuSurge Projects`} description={project.description} />
      <section className="relative overflow-hidden bg-brand-950">
        <img src={project.image} alt={project.name} className="h-[42vh] min-h-[280px] w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-brand-950/60 to-transparent" aria-hidden="true" />
        <div className="container-x absolute inset-x-0 bottom-0 pb-10">
          <span className="eyebrow text-accent">{project.facility_type}</span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl md:text-5xl font-semibold tracking-tighter text-white" data-testid="project-title">{project.name}</h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x grid gap-14 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <p className="text-base md:text-lg leading-relaxed text-slate-600" data-testid="project-description">{project.description}</p>
            {project.services?.length > 0 && (
              <div className="mt-10" data-testid="project-services">
                <h2 className="font-heading text-xl font-semibold text-ink">Services Provided</h2>
                <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                  {project.services.map((s) => (
                    <li key={s} className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {project.equipment?.length > 0 && (
              <div className="mt-10" data-testid="project-equipment">
                <h2 className="font-heading text-xl font-semibold text-ink">Equipment / Infrastructure</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {project.equipment.map((e) => <span key={e} className="chip">{e}</span>)}
                </ul>
              </div>
            )}
            <div className="mt-10 overflow-hidden rounded-xl" data-testid="project-gallery">
              <img src={project.image} alt={`${project.name} — gallery`} loading="lazy" className="h-[320px] w-full object-cover" />
            </div>
          </div>

          <aside>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 lg:sticky lg:top-28" data-testid="project-facts">
              <h2 className="font-heading text-lg font-semibold text-ink">Project Overview</h2>
              <dl className="mt-6 space-y-5">
                {facts.map(([k, v]) => (
                  <div key={k} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{k}</dt>
                    <dd className="mt-1 text-sm text-slate-700" data-testid={`project-fact-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v || "[To be updated]"}</dd>
                  </div>
                ))}
              </dl>
              <Link to="/contact" className="btn-accent mt-8 w-full" data-testid="project-enquire">
                Discuss a Similar Project <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <CTABand />
    </main>
  );
};

export default ProjectDetail;
